import { SchedulerInterval } from '@jupiterone/jupiter-types';
import {
  CloseButton,
  JSONEditor,
  JupiterTheme,
  useSnackbarNotification,
  useIntegrationsPollingIntervalValueSetEntitlements,
  QuestionRuleInstanceWithTags,
} from '@jupiterone/jupiter-web-toolkit';
import {
  Button,
  CircularProgress,
  Modal,
  Paper,
  Typography,
} from '@material-ui/core';
import { createStyles, makeStyles } from '@material-ui/styles';
import React, { useEffect, useState } from 'react';
import { useBooleanState } from 'react-use-object-state';
import { SanitizedQuestionRuleInstance } from 'jupiter-web-alerts/src/types';
import BasicRuleEditor from './editors/BasicRuleEditor';
import { checkBeforeSubmitting } from './utils/ruleValidators';
import useRulesList from '@jupiterone/jupiter-web-toolkit/src/context/useRulesList';

const useStyles = makeStyles((theme: JupiterTheme) =>
  createStyles({
    paper: {
      position: 'absolute',
      width: '80vw',
      height: '80vh',
      backgroundColor: theme.palette.background.paper,
      boxShadow: theme.shadows[5],
      padding: theme.spacing(2.5),
      top: '50%',
      left: '50%',
      transform: 'translate(-50%, -50%)',
      display: 'flex',
      flexDirection: 'column',
      outline: 'none',
      overflowY: 'auto',
    },
    title: {
      color: theme.palette.primary.main,
    },
    buttons: {
      display: 'flex',
      justifyContent: 'flex-end',
      margin: 0,
      padding: theme.spacing(3, 0.5, 0.5, 3),
      width: '100%',
      '& > button': {
        margin: theme.spacing(0, 1),
        '&:focus': {
          textDecoration: 'none',
        },
      },
    },
    editor: {
      flex: '1 1 100%',
      overflowY: 'auto',
      margin: theme.spacing(2, 0, 0),
    },
    topButtons: {
      display: 'flex',
      alignItems: 'flex-start',
      '& button': {
        lineHeight: 1.35,
        '&:focus': {
          textDecoration: 'none',
        },
      },
    },
    modalHeader: {
      display: 'flex',
      justifyContent: 'space-between',
    },
    modalFooter: {
      flex: 1,
      display: 'flex',
      justifyContent: 'flex-end',
      padding: theme.spacing(3, 0.5, 0.5, 3),
      '& button': {
        margin: theme.spacing(0, 1),
        minWidth: 75,
      },
    },
    cancelButton: {
      '&:focus': {
        textDecoration: 'none',
      },
    },
    closeButton: {
      flex: 1,
      paddingTop: 0,
      paddingRight: 0,
      paddingBottom: 0,
    },
  })
);

export interface RuleModalProps {
  title: string;
  readonly?: boolean;
  open: boolean;
  rule: SanitizedQuestionRuleInstance;
  onSubmit: (rule: QuestionRuleInstanceWithTags) => void;
  onClose: () => void;
  busy?: boolean;
}

const RuleModal = ({
  title,
  readonly,
  busy,
  open,
  onSubmit,
  onClose,
  rule,
}: RuleModalProps) => {
  const classes = useStyles();
  const notifications = useSnackbarNotification();
  const showAdvanced = useBooleanState(false);
  const advancedDisabled = useBooleanState(false);
  const [content, setContent] = useState<string>('');
  const {
    data: integrationsPollingIntervalValueSetEntitlements,
  } = useIntegrationsPollingIntervalValueSetEntitlements();
  const { tags } = useRulesList();

  const hasAccessToPollingInterval = (increment: SchedulerInterval) => {
    if (!increment) {
      return false;
    }
    return !!integrationsPollingIntervalValueSetEntitlements?.checkValueSetEntitlementAccess.items.find(
      (item) => item.value === SchedulerInterval[increment] && item.canAccess
    );
  };

  const alterContentsIfUserMissingPollingInterval = (
    alteredContent: QuestionRuleInstanceWithTags
  ) => {
    const { pollingInterval } = alteredContent;

    if (pollingInterval && hasAccessToPollingInterval(pollingInterval)) {
      return { content: alteredContent };
    }

    const providedWithDefault = alteredContent;
    providedWithDefault.pollingInterval = SchedulerInterval['ONE_WEEK'];

    setContent(JSON.stringify(providedWithDefault, null, 2));
    return {
      content: providedWithDefault,
      helpText: `You don't have access to ${pollingInterval} intervals, setting to ONE_WEEK`,
    };
  };

  const getParsedRule = () => {
    try {
      return JSON.parse(content || '{}') as QuestionRuleInstanceWithTags;
    } catch (error) {
      notifications.warning(error.message);
      showAdvanced.setTrue();
      return {} as any;
    }
  };

  useEffect(() => {
    if (open) {
      setContent(JSON.stringify(rule, null, 2));
    }
    if (rule.question.queries.length > 1 || rule.operations?.length > 1) {
      advancedDisabled.setTrue();
    }
  }, [rule, open]);

  const handleSubmit = () => {
    try {
      const parsedRule = alterContentsIfUserMissingPollingInterval(
        JSON.parse(content)
      );

      const result = checkBeforeSubmitting(parsedRule.content);
      if (!result.length && !parsedRule.helpText) {
        onSubmit(parsedRule.content);
      } else {
        if (parsedRule.helpText) {
          result.push({ helpText: parsedRule.helpText, error: false });
        }
        notifications.warning(result.map((e) => e.helpText).join(','));
      }
    } catch (error) {
      notifications.warning(error.message);
    }
  };

  const handleBasicUpdate = (basicRule: QuestionRuleInstanceWithTags) => {
    try {
      setContent(JSON.stringify(basicRule, null, 2));
    } catch (error) {
      notifications.error({ error, message: error.message });
    }
  };

  const updateContent = (content: string) => {
    setContent(content);
    try {
      JSON.parse(content);
    } catch (error) {
      notifications.warning(error.message);
    }
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Paper data-testid="RuleEditModal-Body" className={classes.paper}>
        <div className={classes.modalHeader}>
          <Typography variant="h6" className={classes.title}>
            {title}
          </Typography>
          <div className={classes.topButtons}>
            {!readonly && (
              <Button
                disabled={advancedDisabled.state}
                onClick={showAdvanced.toggle}
                size="small"
              >
                {showAdvanced.state ? 'Show Visual' : 'Show Advanced'}
              </Button>
            )}
            <CloseButton className={classes.closeButton} onClose={onClose} />
          </div>
        </div>
        <div
          className={classes.editor}
          data-testid={
            showAdvanced.state || advancedDisabled.state
              ? 'JSONEditor'
              : 'BasicRuleEditor'
          }
        >
          {showAdvanced.state || advancedDisabled.state ? (
            <>
              <JSONEditor value={content} onChange={updateContent} />
            </>
          ) : (
            <BasicRuleEditor
              readonly={readonly}
              rule={getParsedRule()}
              onChange={handleBasicUpdate}
              tags={tags?.questionRuleTags}
            />
          )}
        </div>
        {!readonly && (
          <div className={classes.modalFooter}>
            <Button onClick={onClose} className={classes.cancelButton}>
              Cancel
            </Button>
            <Button
              title="Save"
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={busy}
              data-testid="rule-modal-save-btn"
            >
              {busy ? <CircularProgress size={20} /> : 'Save'}
            </Button>
          </div>
        )}
      </Paper>
    </Modal>
  );
};

export default RuleModal;
